import { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthService } from "../../services/authService";
import { Toast } from "primereact/toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import { InputOtp } from "primereact/inputotp";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchMaintenanceStatus } from "../../redux/slices/maintenanceSlice";

const OtpPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [timer, setTimer] = useState(30);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [email, setEmail] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const toastRef = useRef<Toast>(null);
  const [roleCode, setRoleCode] = useState<string | null>(null);

  const dispatch: AppDispatch = useDispatch();
  const { isInMaintenance } = useSelector(
    (state: RootState) => state.maintenance
  );

  useEffect(() => {
    dispatch(fetchMaintenanceStatus());
  }, [dispatch]);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const currentUser = await AuthService.getCurrentUser();
        const roleCode = currentUser?.data?.role?.role_code;
        setRoleCode(roleCode);
      } catch (err) {
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch user data.",
          life: 3000,
        });
        navigate("/auth", { replace: true });
      }
    };

    fetchCurrentUser();
  }, []);

  useEffect(() => {
    const state = location.state as { email: string | null };
    if (state?.email) {
      setEmail(state.email);
    }
  }, [location.state]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(
        () => setTimer((prevTimer) => prevTimer - 1),
        1000
      );
      return () => clearInterval(interval);
    } else {
      setResendEnabled(true);
    }
  }, [timer]);

  const otpValidationSchema = Yup.object().shape({
    otp: Yup.string()
      .length(6, "OTP must be exactly 6 digits")
      .matches(/^\d+$/, "OTP must contain only digits")
      .required("OTP is required"),
  });

  const formik = useFormik({
    initialValues: { otp: "" },
    validationSchema: otpValidationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        const response = await AuthService.verifyOtp({
          email,
          otp: values.otp,
        });
        if (response?.status === 2000) {
          toastRef.current?.show({
            severity: "success",
            summary: "Success",
            detail: "OTP verified successfully!",
            life: 3000,
          });
          localStorage.setItem("otpConfirmed", "true");

          // Redirect CUSTOMER to maintenance page if maintenance mode is active
          if (roleCode === "CUSTOMER" && isInMaintenance) {
            navigate("/maintenance", { replace: true });
            return;
          }

          if (roleCode == "CUSTOMER" || roleCode === "ENTREPRISE") {
            navigate("/dashboard");
          } else {
            navigate("/admin/customers");
          }
        } else {
          toastRef.current?.show({
            severity: "error",
            summary: "Error",
            detail: response?.message || "OTP verification failed.",
            life: 3000,
          });
        }
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message || "An error occurred. Please try again.";
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
          life: 3000,
        });
      } finally {
        setSubmitting(false);
        setTimer(30);
        setResendEnabled(false);
      }
    },
  });

  const handleResendOtp = async () => {
    setResendEnabled(false);
    setTimer(30);
    try {
      await AuthService.sendOtp(email);
      toastRef.current?.show({
        severity: "info",
        summary: "OTP Sent",
        detail: "A new OTP has been sent to your email.",
        life: 3000,
      });
    } catch (err: any) {
      const error =
        err.response?.data?.message ||
        "Failed to resend OTP. Please try again.";
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: error,
        life: 3000,
      });
      setResendEnabled(true);
    }
  };

  return (
    <div className="mt-5 otp-verify">
      <Toast ref={toastRef} className="toast-top-center" />
      <div className=" bg-white p-4 rounded-lg shadow-sm max-w-md mx-auto">
        <div className="flex flex-col items-center mt-5">
          <h4 className="text-2xl mb-1 font-bold">Account verification</h4>
          <p className="mb-6 text-slate-500 text-sm">
            Enter the OTP sent to your email.
          </p>
        </div>
        <form
          onSubmit={formik.handleSubmit}
          className="flex flex-col items-center mt-5"
        >
          <div className="mb-8 flex justify-center">
            <InputOtp
              value={formik.values.otp}
              className={" w-full p-2 border rounded-md"}
              onChange={(e) => formik.setFieldValue("otp", e.value)}
              integerOnly
              length={6}
            />
          </div>
          <Button
            label="Verify OTP"
            type="submit"
            loading={submitting}
            className="mt-2 w-full p-2 text-xl font-bold text-white rounded bg-primary-500 hover:bg-orange-600"
            style={{ width: "calc(60 * 6 * 1px)" }}
            disabled={submitting || !formik.isValid}
          />
          <div className="mt-5 text-center text-sm">
            <p className="mb-0 mr-1">
              Didn't receive the OTP?{" "}
              {resendEnabled ? (
                <button
                  onClick={handleResendOtp}
                  className="text-primary-600 fw-semibold"
                >
                  Resend Code
                </button>
              ) : (
                <span className="text-gray-500">Resend OTP in {timer}s</span>
              )}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OtpPage;
